import { memo, ForwardedRef, forwardRef, ReactNode, useState } from 'react'
import { Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { graphql, useFragment } from 'react-relay'
import MoreButtonMenu from 'components/MoreButtonMenu'
import Tag from 'components/Tag'
import FocusFadeGroup from 'components/FocusFadeGroup'
import Suspense from 'components/Suspense'
import { ArrowDropDown } from '@mui/icons-material'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useSnackbar } from 'hooks/redux'
import { IndustryField_UpdateUserMutation } from '__generated__/IndustryField_UpdateUserMutation.graphql'
import { IndustryField_User$key } from '__generated__/IndustryField_User.graphql'
import useStyles from './styles'
import IndustriesList from './IndustriesList'

interface IIndustryFieldProps {
  editable?: boolean
  user: IndustryField_User$key
}

function IndustryField(props: IIndustryFieldProps, ref: ForwardedRef<HTMLButtonElement>) {
  const { classes } = useStyles()
  const theme = useTheme()
  const { editable, user: userFragmentRef } = props

  const user = useFragment(graphql`
    fragment IndustryField_User on User {
      profile {
        industry {
          id
          name
        }
      }
    }
  `, userFragmentRef)

  const [industryId, setIndustryId] = useState<string | null>(user?.profile?.industry?.id || null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const { showSnackbarMessage } = useSnackbar()

  const { execute } = useQuickCommit<IndustryField_UpdateUserMutation>(graphql`
    mutation IndustryField_UpdateUserMutation($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          profile {
            industry {
              id
              name
              profilesRequireSkills # needed for skill selection validation
            }
          }
  
          # Changing industry can cause skills and specializations to change
          ...SpecializationsFields_User
          ...UserSkillsFields_User
        }
      }
    }
  `)

  const handleChange = (value: string | null) => {
    execute({
      input: {
        profile: {
          industryId: value,
        },
      },
    }).then((result) => {
      const newValue = result?.updateUser?.user?.profile?.industry?.id

      if (newValue) {
        setSaveError(null)
        setIndustryId(newValue)
        showSnackbarMessage('Area of expertise updated', { level: 'success' })
      }
    })
  }

  const validationError = user?.profile?.industry || !editable ? null : 'Required'
  const error = saveError || validationError
  const isMobile = useResponsiveBreakpoint(theme.breakpoints.values.sm)
  const industry = user?.profile?.industry

  if (!industry && !editable) {
    return null
  }

  let editButton: ReactNode
  let errorText: ReactNode

  if (editable) {
    editButton = (
      <MoreButtonMenu
        ref={ref}
        tooltip="Edit area of expertise"
        icon={<ArrowDropDown />}
        data-cy="profile-industry-edit"
        className={classes.edit}
      >
        <Suspense>
          <IndustriesList
            selectedValue={industryId}
            onChange={handleChange}
          />
        </Suspense>
      </MoreButtonMenu>
    )

    if (error) {
      errorText = (
        <Typography variant="body1" className={classes.error} data-cy="profile-industry-error">
          {error}
        </Typography>
      )
    }
  }

  if (industry) {
    return (
      <Tag className={classes.container} data-cy="profile-freelancer-type">
        <FocusFadeGroup focused={false} style={{ display: 'inline-block' }}>
          <Typography variant="h3" component="span">
            {industry?.name}
          </Typography>

          {editButton}
          {errorText}
        </FocusFadeGroup>
      </Tag>
    )
  }

  return (
    <Tag className={classes.container}>
      <FocusFadeGroup focused={false} style={{ display: 'inline-block' }}>
        <span style={{ opacity: 0.5 }}>
          {isMobile ? 'Area of Expertise' : 'Select Your Area of Expertise'}
        </span>

        {editButton}
        {errorText}
      </FocusFadeGroup>
    </Tag>
  )
}

export default memo(forwardRef(IndustryField))
